import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { IHandleResponseError } from "@/Common/Api/IHandleResponseError";
import { Guid } from "@/Common/Models/Guid";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { PatientStore } from "@/Patient/Stores/PatientStore";

/**
 * An error handler which applies encounter-specific response validation.
 */
class EncounterDeletedHandler implements IHandleResponseError {
    private patientStore: PatientStore;

    constructor(patientStore: PatientStore) {
        this.patientStore = patientStore;
    }

    /**
     * @inheritdoc
     */
    public handleError(response: ServerEvent): boolean {
        if (response != null && response.Errors != null && response.Errors.length > 0) {
            if (this.isEncounterDeletedResponse(response)) {
                this.displayDeletedEncounterWarning(this.patientStore, this.getEncounterIdFromDeletedError(response));
                patientStore.EmitChangeToListeners();
                return false;
            }
        }

        return true;
    }

    private isEncounterDeletedResponse(response: ServerEvent): boolean {
        return (response != null && response.Errors.some(e => e.Code == "EncounterDeletedException"));
    }

    private getEncounterIdFromDeletedError(response: ServerEvent): string {
        if (response != null) {
            let errors: ErrorMessage[] = response.Errors.filter(x => x.Code = "EncounterDeletedException");
            if (errors && errors.length > 0) {
                return errors[0].Message;
            }
        }
        return null;
    }

    private displayDeletedEncounterWarning(store: PatientStore, encounterId: Guid | string): void {
        let currentPatientId = store.getCurrentPatientContactId();

        // if the warning is no longuer for the current encounter or if the patient doesn't even have this encounter in his summary,
        // we don't care about showing the popup and return
        // else we show the warning
        if (
            store.PatientMedicalNoteModels[currentPatientId] == null
            || !store.PatientMedicalSummaryModels[currentPatientId]?.get("Encounters").some(e => e.Id == encounterId)
        )
            return;

        // display warning popup
        let patientUIModel = store.PatientUIModels[patientStore.getCurrentPatientContactId()];
        if (patientUIModel) {
            patientUIModel = patientUIModel.set("ShowEncounterWasDeletedWarning", true);
            store.PatientUIModels[currentPatientId] = patientUIModel;
        }

        // close other potenrial popups
        let patientNoteModel = store.PatientMedicalNoteModels[currentPatientId];
        if (patientNoteModel) {
            patientNoteModel = patientNoteModel.set("IsNotePopupOpen", false);

            patientNoteModel = patientNoteModel.set("IsLabFaxPopupOpen", false);
            patientNoteModel = patientNoteModel.set("IsPrivateBillEditPopupOpen", false);
            patientNoteModel = patientNoteModel.set("IsPrivateBillPopupOpen", false);
            patientNoteModel = patientNoteModel.set("IsConsultationRequestFaxPopupOpen", false);
            patientNoteModel = patientNoteModel.set("IsEditConsultationDetailsPopupVisible", false);
            store.PatientMedicalNoteModels[currentPatientId] = patientNoteModel;
        }
    }
}

export { EncounterDeletedHandler };
